// One document-owned menu; listeners exist only while open. DOM/selection owners
// decide whether a render may restore it. No transport or account state here.
type MenuPoint = { x: number; y: number };
type SavedMenu = { key: string; focus: number; point?: MenuPoint };
let active: { point?: MenuPoint; key: string; trigger: HTMLButtonElement; menu: HTMLDivElement; close: (restore?: boolean) => void } | null = null;
export function suspendMenu(): SavedMenu | null {
  if (!active) return null;
  const saved = { key: active.key, point: active.point, focus: [...active.menu.children].indexOf(document.activeElement as Element) };
  active.close(); return saved;
}
export function restoreMenu(saved: ReturnType<typeof suspendMenu>): void {
  if (!saved) return;
  const trigger = [...document.querySelectorAll<HTMLButtonElement>('[data-menu-key]')].find(item => item.dataset.menuKey === saved.key);
  trigger?.dispatchEvent(new CustomEvent('restore-menu', { detail: saved }));
}
export function pageZoom(): number {
  return Number.parseFloat(getComputedStyle(document.documentElement).zoom) || 1;
}
// Moves a fixed element so its viewport box starts at (left, top) viewport pixels. The page is
// CSS-zoomed (:root zoom 90%) and browsers disagree on how that zoom maps a fixed element's
// left/top to viewport pixels (Firefox showed the composer "+" menu far from its button while
// dividing by pageZoom() suited Chromium), so measure the actual mapping instead of assuming it.
export function placeFixed(el: Pick<HTMLElement, 'style' | 'getBoundingClientRect'>, left: number, top: number): void {
  const PROBE = 100;
  el.style.left = '0px'; el.style.top = '0px';
  const origin = el.getBoundingClientRect();
  el.style.left = `${PROBE}px`; el.style.top = `${PROBE}px`;
  const moved = el.getBoundingClientRect();
  const scaleX = (moved.left - origin.left) / PROBE || 1; const scaleY = (moved.top - origin.top) / PROBE || 1;
  el.style.left = `${(left - origin.left) / scaleX}px`; el.style.top = `${(top - origin.top) / scaleY}px`;
}
export function actionMenu(label: string, key: string, menu: HTMLDivElement, triggerClass = '', triggerText = '⋯'): HTMLDivElement {
  const host = document.createElement('div'); host.className = 'action-menu relative shrink-0';
  const trigger = document.createElement('button'); trigger.type = 'button'; trigger.textContent = triggerText;
  trigger.className = `menu-trigger ${triggerClass}`; trigger.setAttribute('aria-label', label); trigger.dataset.menuKey = key;
  trigger.setAttribute('aria-haspopup', 'menu'); trigger.setAttribute('aria-expanded', 'false');
  // Derived from the (document-unique) key, not a counter: an unchanged render must produce an
  // identical node so the app can keep the mounted one (see mountShell() in app.ts).
  menu.id = `action-menu-${key}`; trigger.setAttribute('aria-controls', menu.id);
  menu.className = 'action-menu-panel'; menu.setAttribute('role', 'menu'); menu.setAttribute('aria-label', label); menu.hidden = true; menu.popover = 'manual';
  const items = [...menu.querySelectorAll<HTMLButtonElement>('button')];
  for (const item of items) { item.setAttribute('role', 'menuitem'); item.tabIndex = -1; }
  let listeners: AbortController | null = null;
  const close = (restore = false): void => {
    listeners?.abort(); listeners = null;
    if (menu.matches(':popover-open')) menu.hidePopover();
    // Drop the open-time position too: a closed menu must serialize exactly like a freshly
    // built one, or its whole region would never compare equal again (see mountShell()).
    menu.hidden = true; menu.removeAttribute('style');
    trigger.setAttribute('aria-expanded', 'false');
    if (active?.menu === menu) active = null;
    if (restore && trigger.isConnected) trigger.focus({ preventScroll: true });
  };
  const open = (focus = 0, point?: MenuPoint): void => {
    if (!host.isConnected) return;
    active?.close(); menu.hidden = false; menu.showPopover(); trigger.setAttribute('aria-expanded', 'true');
    // Computed in viewport pixels next to the trigger; placeFixed() handles the page zoom.
    const box = point ? { left: point.x, right: point.x, top: point.y, bottom: point.y } : trigger.getBoundingClientRect(); const own = menu.getBoundingClientRect();
    const left = Math.max(8, Math.min(box.right - own.width, innerWidth - own.width - 8));
    // Open upwards when there is no room below (e.g. the trigger in the sidebar's bottom account row).
    const below = box.bottom + 4 + own.height <= innerHeight - 8;
    const top = Math.max(8, below ? box.bottom + 4 : Math.min(box.top - own.height - 4, innerHeight - own.height - 8));
    placeFixed(menu, left, top);
    active = { key, trigger, menu, close, point }; listeners = new AbortController();
    document.addEventListener('pointerdown', event => { if (!host.contains(event.target as Node)) close(); }, { capture: true, signal: listeners.signal });
    document.addEventListener('focusin', event => { if (!host.contains(event.target as Node)) close(); }, { signal: listeners.signal });
    window.addEventListener('resize', () => close(), { signal: listeners.signal });
    if (focus >= 0) (items[focus]?.disabled ? items.find(item => !item.disabled) : items[focus])?.focus({ preventScroll: true });
  };
  trigger.addEventListener('click', () => menu.hidden ? open() : close(true));
  trigger.addEventListener('open-menu', event => {
    if (menu.hidden) open(0, (event as CustomEvent<{ x: number; y: number }>).detail);
  });
  trigger.addEventListener('restore-menu', event => {
    const saved = (event as CustomEvent<SavedMenu>).detail;
    open(saved.focus, saved.point);
  });
  trigger.addEventListener('keydown', event => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); event.stopPropagation(); open(event.key === 'ArrowDown' ? 0 : items.length - 1); }
  });
  host.addEventListener('keydown', event => {
    if (menu.hidden) return;
    if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(true); return; }
    if (event.key === 'Tab') { close(true); return; }
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const enabled = items.filter(item => !item.disabled); const index = enabled.indexOf(document.activeElement as HTMLButtonElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
    enabled[next]?.focus({ preventScroll: true });
  });
  // Close before the item's handler renders/opens a dialog; never steal its focus.
  menu.addEventListener('click', event => { if ((event.target as HTMLElement).closest('button:not(:disabled)')) close(true); }, true);
  host.append(trigger, menu); return host;
}
